import { Vector2, Raycaster } from 'three';
import { meshSelectionStore } from 'Domain/Mesh/State/MeshSelectionStore';
import { threeEngine } from 'Domain/Scene/Engine/ThreeEngine';
import { Store } from 'Application/Store/Store';
import { SET_SELECTED_TOOTH } from 'Application/Store/Actions/ToothTypes';
import { buildTeethOutline, destroyTeethOutline } from './Composer.js';
import { getViewports } from "../../Scene/Handler/ViewportHandler";
import { setKeyframeToEnd, toogleAnimationVisible } from 'Domain/Scene/Manager/SceneManager.js';
import { getTimes } from 'Domain/Scene/Handler/AnimationHandler';
import { updateAllMovementsDatas } from "../Movement/ToothMovementTracker";
import { computeAndDisplayTeethIntersection } from "../../Scene/Handler/CollisionHandler";

let selectionModeEnabled = false;
let isAnimationToEnd = false;

const sharedRaycaster = new Raycaster();

/* ──────────────────────────────────────────────
   SELECTION MODE STATE
   ────────────────────────────────────────────── */

export const isSelectionModeEnabled = () => selectionModeEnabled;

export const setAnimationToEnd = (value) => { isAnimationToEnd = value; };

export const enableSelectionMode = () => {
    jumpToAnimationEndIfNeeded();
    if (!selectionModeEnabled) selectionModeEnabled = true;
};

const jumpToAnimationEndIfNeeded = () => {
    if (isAnimationToEnd) return;
    Store.dispatch(setKeyframeToEnd(getTimes()));
    Store.dispatch(toogleAnimationVisible('none'));
    isAnimationToEnd = true;
};

export const disableSelectionMode = () => {
    if (!selectionModeEnabled) return;
    selectionModeEnabled = false;
    isAnimationToEnd = false;
    dispatchToothSelection(null);
    meshSelectionStore.clearClickedTooth();
};

/* ──────────────────────────────────────────────
   DOUBLE-CLICK / DOUBLE-TAP HANDLER (Tooth Selection)
   - Desktop mouse: native dblclick event (unchanged, proven reliable)
   - Tablet touch: pointerdown double-tap detector, because TrackballControls
     calls preventDefault() on touchstart which blocks the browser from
     synthesizing dblclick on touch devices
   ────────────────────────────────────────────── */

const DOUBLE_TAP_THRESHOLD = 400
const DOUBLE_TAP_DISTANCE = 25

let _lastTap = { time: 0, x: 0, y: 0 }

const detectDoubleTap = (event) => {
    const now = Date.now()
    const dx = event.clientX - _lastTap.x
    const dy = event.clientY - _lastTap.y
    const elapsed = now - _lastTap.time
    _lastTap.time = now
    _lastTap.x = event.clientX
    _lastTap.y = event.clientY
    return elapsed < DOUBLE_TAP_THRESHOLD && Math.abs(dx) < DOUBLE_TAP_DISTANCE && Math.abs(dy) < DOUBLE_TAP_DISTANCE
}

const isVisibleInScene = (obj) => {
    let current = obj;
    while (current) {
        if (!current.visible) return false;
        current = current.parent;
    }
    return true;
};

const performSelection = (event, renderer, controls, allObjects, allToothObjects, maxiExtraData, mandiExtraData) => {
    const canvasBounds = renderer.domElement.getBoundingClientRect()
    const { camera, normalizedMouse } = getCameraAndMouseFromClick(event, canvasBounds, controls)
    const visibleObjects = allObjects.filter(obj => isVisibleInScene(obj));
    const visibleToothObjects = allToothObjects.filter(tooth => isVisibleInScene(tooth.refObj));
    const selectableObjects = getSelectableObjectsForCamera(camera, visibleObjects, maxiExtraData, mandiExtraData)
    const intersectedTooth = findIntersectedTooth(normalizedMouse, camera, selectableObjects, visibleToothObjects)
    handleToothSelection(intersectedTooth)
}

export const registerDoubleClickHandler = (canvas, renderer, controls, allObjects, allToothObjects, maxiExtraData, mandiExtraData) => {
    canvas.addEventListener('dblclick', (event) => {
        if (!isSelectionModeEnabled()) return
        performSelection(event, renderer, controls, allObjects, allToothObjects, maxiExtraData, mandiExtraData)
    })

    canvas.addEventListener('pointerdown', (event) => {
        if (!isSelectionModeEnabled()) return
        if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return

        if (!detectDoubleTap(event)) return
        _lastTap.time = 0

        performSelection(event, renderer, controls, allObjects, allToothObjects, maxiExtraData, mandiExtraData)
    })
}

/* ──────────────────────────────────────────────
   RAYCASTING UTILITIES
   ────────────────────────────────────────────── */

const getCameraAndMouseFromClick = (event, canvasBounds, controls) => {
    const viewportIndex = calculateViewportIndex(event, canvasBounds);
    const normalizedMouse = calculateNormalizedMousePosition(event, canvasBounds, viewportIndex);
    const viewportName = getViewports()[viewportIndex].name;
    const camera = threeEngine.getCamera(viewportName);

    if (controls && viewportName !== 'top' && viewportName !== 'bottom') {
        controls.object = camera;
    }
    return { camera, normalizedMouse };
};

const calculateViewportIndex = (event, canvasBounds) => {
    const sectionWidth = canvasBounds.width / getViewports().length;
    return Math.floor((event.clientX - canvasBounds.left) / sectionWidth);
};

const calculateNormalizedMousePosition = (event, canvasBounds, viewportIndex) => {
    const sectionWidth = canvasBounds.width / getViewports().length;
    const localX = event.clientX - canvasBounds.left - (viewportIndex * sectionWidth);
    return new Vector2(
        (localX / sectionWidth) * 2 - 1,
        -((event.clientY - canvasBounds.top) / canvasBounds.height) * 2 + 1
    );
};

const getSelectableObjectsForCamera = (camera, allObjects, maxiExtraData, mandiExtraData) => {
    const cameraSelectableObjectsMap = {
        'TopCamera': () => extractRefObjects(mandiExtraData),
        'BottomCamera': () => extractRefObjects(maxiExtraData),
        'ComparativeCamera': () => [],
        'FrontCamera': () => allObjects
    };
    const cameraName = camera.name || '';
    const selector = Object.keys(cameraSelectableObjectsMap).find(key => cameraName.includes(key));
    return selector ? cameraSelectableObjectsMap[selector]() : allObjects;
};

const extractRefObjects = (extraData) => Object.values(extraData).map(value => value.refObj);

const findIntersectedTooth = (normalizedMouse, camera, selectableObjects, allToothObjects) => {
    sharedRaycaster.setFromCamera(normalizedMouse, camera);
    sharedRaycaster.layers.mask = camera.layers.mask;
    const intersects = sharedRaycaster.intersectObjects(selectableObjects, true);

    if (intersects.length === 0) return null;

    const firstValid = intersects.find(intersection =>
        intersection.object.userData?.toothID || intersection.object.parent?.userData?.toothID
    );
    if (!firstValid) return null;

    const target = firstValid.object.userData?.toothID ? firstValid.object : firstValid.object.parent;
    return allToothObjects.find(tooth => tooth.refObj === target) || null;
};

/* ──────────────────────────────────────────────
   TOOTH SELECTION LOGIC
   ────────────────────────────────────────────── */

const handleToothSelection = (intersectedTooth) => {
    if (!intersectedTooth) {
        deselectCurrentTooth();
        return;
    }
    if (isSameToothAlreadySelected(intersectedTooth)) {
        deselectCurrentTooth();
        return;
    }
    selectTooth(intersectedTooth);
};

const isSameToothAlreadySelected = (tooth) => meshSelectionStore.getClickedTooth() === tooth;

const deselectCurrentTooth = () => {
    meshSelectionStore.clearClickedTooth();
    dispatchToothSelection(null);
    destroyTeethOutline();
    computeAndDisplayTeethIntersection();
};

const selectTooth = (tooth) => {
    meshSelectionStore.setClickedTooth(tooth);
    const toothId = extractToothId(tooth);
    dispatchToothSelection(toothId);

    buildTeethOutline(tooth.refObj);
    setupToothForTransformation(tooth);
    saveInitialMovementIfNeeded(tooth);

    updateAllMovementsDatas(tooth);
};

const extractToothId = (tooth) => tooth.refObj.name.split('_').pop();

const dispatchToothSelection = (toothId) => {
    Store.dispatch({
        type: SET_SELECTED_TOOTH,
        payload: { id: toothId }
    });
};

const setupToothForTransformation = (tooth) => {
    attachToothToPivot(tooth);
};

const attachToothToPivot = (tooth) => {
    tooth.pivot.add(tooth.refObj);
    if (!tooth.pivotAlreadyPlaced) {
        tooth.placePivot();
    }
};

const saveInitialMovementIfNeeded = (tooth) => {
    if (tooth.mouvements.length === 0) {
        tooth.pivot.updateMatrix();
        tooth.mouvements.push(tooth.pivot.matrix.clone());
    }
    tooth.pivotAlreadyPlaced = true;
};
